import React, { useEffect, useState } from 'react'
import { ReactComponent as CloseIcon } from '../../assets/Close-btn.svg'
import { Checkbox, FormControlLabel, InputAdornment, OutlinedInput } from '@mui/material';
import { ReactComponent as EyeIcon } from '../../assets/Eye.svg'
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../../firebase/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../AppContext';
import { UserService } from '../../services/UserService';
import { getOrCreateBrowserId } from '../../utils/browserId';
import i18n from '../../locales/i18n';
import { useTranslation } from 'react-i18next';

interface LoginProps {
  onClose: () => void
  setType: (value: string) => void
}

const Login: React.FC<LoginProps> = ({ onClose, setType }) => {
  const [isRTL, setIsRTL] = useState<boolean>(i18n.language === 'ar');
  const { t } = useTranslation();
  const { authLoading, userDataLoading, setUser, setShowModal } = useAppContext();

  useEffect(() => {
    const handleLanguageChange = () => {
      setIsRTL(i18n.language === 'ar');
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [rememberMeChecked, setRememberMeChecked] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { role, setModalMode, logoutNotActive } = useAppContext();

  const handleRememberMeChange = () => {
    setRememberMeChecked(!rememberMeChecked);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPasswordClick = () => {
    setModalMode('forgotPassword');
  };

  // const handleLogin = async () => {
  //   if (!email || !password) {
  //     setErrorMessage(t('auth.validationError'));
  //     return;
  //   }

  //   setLoading(true);
  //   setErrorMessage('');
  //   try {
  //     const browserId = getOrCreateBrowserId();
  //     const persistence = rememberMeChecked ? browserLocalPersistence : browserSessionPersistence;
  //     await setPersistence(auth, persistence);
  //     await signInWithEmailAndPassword(auth, email, password);
      
  //     // Wait for auth state to be updated
  //     await new Promise((resolve) => {
  //       const unsubscribe = onAuthStateChanged(auth, async (user) => {
  //         if (user) {
  //           try {
  //             const userData = await UserService.getUser();
  //             setUser(userData);
  //             unsubscribe();
  //             resolve(true);
  //           } catch (error) {
  //             console.error('Error fetching user data:', error);
  //             unsubscribe();
  //             resolve(false);
  //           }
  //         }
  //       });
  //     });

  //     // Close modal and navigate
  //     onClose();
  //     setShowModal(false);
  //     navigate('/profile?page=My Library');
  //   } catch (error) {
  //     setErrorMessage(t('auth.loginError'));
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Sign in with Google
  
  const handleLogin = async () => {
    if (!email || !password) {
      setErrorMessage(t('auth.validationError'));
      return;
    }
    setLoading(true);
    setErrorMessage('');
    try {
      const browserId = getOrCreateBrowserId();
      const persistence = rememberMeChecked ? browserLocalPersistence : browserSessionPersistence;
      await setPersistence(auth, persistence);
      await signInWithEmailAndPassword(auth, email, password);

      const userData = await UserService.getUser();
      await UserService.setUUID(browserId).catch((error) => {
        console.error(error);
      });
      console.log('userData', userData)
      if (
        (!userData.active && userData.role !== 'publisher') ||
        (userData.role === 'publisher' && userData.archived)
      ) {
        logoutNotActive();
        navigate('/');
        return;
      }
      
      navigate(userData.role === 'publisher' ? '/book-store' : '/profile?page=My Library'); 
      onClose();
    } catch (error) {
      console.error("Error logging in:", error);
      setErrorMessage(t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      const userData = await UserService.getUser();
      setUser(userData);
      onClose();
      setShowModal(false);
      navigate('/profile?page=My Library');
    } catch (error) {
      setErrorMessage('Error signing in with Google.');
    }
  };

  // Sign in with Facebook
  const handleFacebookSignIn = async () => {
    try {
      const provider = new FacebookAuthProvider();
      await signInWithPopup(auth, provider);
      const userData = await UserService.getUser();
      setUser(userData);
      onClose();
      setShowModal(false);
      navigate('/profile?page=My Library');
    } catch (error) {
      setErrorMessage('Error signing in with Facebook.');
    }
  };

  // Sign in with Apple
  const handleAppleSignIn = async () => {
    try {
      const provider = new OAuthProvider('apple.com');
      await signInWithPopup(auth, provider);
      const userData = await UserService.getUser();
      setUser(userData);
      onClose();
      setShowModal(false);
      navigate('/profile?page=My Library');
    } catch (error) {
      setErrorMessage('Error signing in with Apple.');
    }
  };


  const handleForgotPassword = () => {
    onClose();
    navigate('/forgot-password');
  };

  return (
    <div>
      <div className='flex flex-col'>
        <label htmlFor="Email" className='font-normal md:text-[18px] mb-[7px] leading-none'>
          {t('auth.email')}
        </label>
        <input
          placeholder={t('auth.emailPlaceholder')}
          className={`input border-[1px] rounded-[5px] border-[rgba(102,102,102,0.35)] px-[12px] md:px-[20px] pt-[8px] md:pt-[10px] pb-[8px] md:pb-[10px] !important ${isRTL ? 'text-right' : 'text-left'}`}
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setErrorMessage('') }}
        />
      </div>

      <div className='flex flex-col mt-[25px]'>
        <label htmlFor="Password" className='font-normal md:text-[18px] mb-[7px] leading-none'>
          {t('auth.password')}
        </label>
        <div className='relative'>
          <input
            placeholder={t('auth.passwordPlaceholder')}
            className={`input border-[1px] rounded-[5px] border-[rgba(102,102,102,0.35)] px-[12px] md:px-[20px] pt-[8px] md:pt-[10px] pb-[8px] md:pb-[10px] !important ${isRTL ? 'text-right' : 'text-left'} w-full`}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <EyeIcon
            className={`cursor-pointer absolute ${isRTL ? 'left-[12px] md:left-[20px]' : 'right-[12px] md:right-[20px]'} top-1/2 transform -translate-y-1/2`}
            onClick={handleClickShowPassword}
          />
        </div>
      </div>

      <div className="flex justify-between mt-[20px] md:mt-[25px]">

        <button className='flex items-center gap-1 cursor-pointer' onClick={handleRememberMeChange}>
          <Checkbox
            name="remember"
            sx={{ color: '#000000' }}
            checked={rememberMeChecked}
            onChange={handleRememberMeChange}
          />

          <p className='text-[14px] md:text-4' style={{ color: '#000000' }}>{t('auth.rememberMe')}</p>

        </button>
        <button className='text-[#00726B] underline text-[14px] md:text-[16px]' onClick={handleForgotPasswordClick} >
          {isRTL ? t('auth.forgotPassword') : `${t('auth.forgotPassword')}?`}
        </button>
      </div>

      <button
        className='bg-[#00726B] font-semibold text-[18px] md:text-[22px] w-full text-white rounded-[12px] md:rounded-[5px] mt-[30px] mb-[45px] py-[18px] md:py-[10px]'
        onClick={handleLogin}
        disabled={loading}
      >
        {loading ? t('auth.loggingIn') : t('auth.signIn')}
      </button>

      {errorMessage && (
        <p className='text-red text-center'>{errorMessage}</p>
      )}

      {/* <div className='flex justify-between gap-[23px]'>
        <button 
          className='border-[1px] border-[#D8DADC] pt-[15px] pb-[14px] rounded-[5px] flex-1 flex items-center justify-center'
          onClick={handleFacebookSignIn}
        >
          <Facebook/>
        </button>
        <button 
          className='border-[1px] border-[#D8DADC] pt-[15px] pb-[14px] rounded-[5px] flex-1 flex items-center justify-center'
          onClick={handleGoogleSignIn}
        >
          <Google/>
        </button>
        <button 
          className='border-[1px] border-[#D8DADC] pt-[15px] pb-[14px] rounded-[5px] flex-1 flex items-center justify-center'
          onClick={handleAppleSignIn}
        >
          <Apple/>
        </button>
      </div> */}

      <p className={`text-[18px] md:text-[20px] w-full text-center ${isRTL ? 'mt-[10px] md:mt-[44px]' : 'mt-[10px] md:mt-[10px]'} text-[#4D4D4D]`}>
        {isRTL ? t('auth.noAccount') : t('auth.dontHaveAccount')}
        <button className='underline text-[#00726B] pl-1 pr-1' onClick={() => { setType("register") }}>
          {t('auth.register')}
        </button>
      </p>
    </div>
  )
}

export default Login;
